/**
 * 键盘适配测试页面
 *
 * 用途：验证 iOS Safari 键盘适配方案是否正常工作
 *
 * 测试步骤：
 * 1. 在 iPad Safari 真机上打开此页面（模拟器无效）
 * 2. 点击输入框，观察键盘弹起时：
 *    - 键盘高度指示器是否显示
 *    - 底部操作栏是否上移（不被键盘遮挡）
 *    - 输入框是否自动滚动到可见位置
 * 3. 测试横屏和竖屏两种模式
 */

import { useRef } from "react";
import { Form, Input, Card, Space, Typography, Tag } from "antd";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useKeyboardAdjustment, useInputScrollIntoView } from "../hooks/useKeyboardAdjustment";
import { isIOSStandaloneMode, getDeviceInfo } from "@/utils/platform";
import CustomButton from "../components/CustomButton";

const { Title, Text } = Typography;

export default function KeyboardTestPage() {
  const navigate = useNavigate();
  const { keyboardHeight, isKeyboardVisible, viewportHeight } = useKeyboardAdjustment();
  const isStandalone = isIOSStandaloneMode();
  const deviceInfo = getDeviceInfo();

  // 为每个输入框创建 ref
  const input1Ref = useRef<any>(null);
  const input2Ref = useRef<any>(null);
  const input3Ref = useRef<any>(null);
  const input4Ref = useRef<any>(null);
  const input5Ref = useRef<any>(null);

  // 启用自动滚动
  useInputScrollIntoView(input1Ref);
  useInputScrollIntoView(input2Ref);
  useInputScrollIntoView(input3Ref);
  useInputScrollIntoView(input4Ref);
  useInputScrollIntoView(input5Ref);

  return (
    <div style={{ height: "var(--viewport-height)", overflow: "hidden", background: "#f5f7fa" }}>
      {/* 顶部导航栏 */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: 60,
          background: "#fff",
          borderBottom: "1px solid #e8e8e8",
          display: "flex",
          alignItems: "center",
          padding: "0 20px",
          zIndex: 100,
        }}
      >
        <CustomButton
          bgColor="transparent"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
        >
          返回
        </CustomButton>
        <Title level={4} style={{ margin: "0 0 0 16px" }}>
          鍵盤適配測試
        </Title>
      </div>

      {/* 键盘状态指示器（调试用） */}
      {isKeyboardVisible && (
        <div
          style={{
            position: "fixed",
            top: 70,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(0, 199, 182, 0.95)",
            color: "#fff",
            padding: "8px 16px",
            borderRadius: 6,
            zIndex: 999,
            fontSize: 14,
            fontWeight: 500,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          鍵盤高度: {keyboardHeight}px | 可視高度: {viewportHeight}px
        </div>
      )}

      {/* 主内容区域 */}
      <div
        className="keyboard-safe-container"
        style={{
          marginTop: 60,
          padding: "20px",
          paddingBottom: 100, // 留出底部操作栏空间
          overflowY: "auto",
          height: "calc(var(--viewport-height) - 60px)",
        }}
      >
        <Card>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <div>
              <Title level={5}>運行環境</Title>
              <Space direction="vertical">
                <div>
                  <Text strong>運行模式: </Text>
                  {isStandalone ? (
                    <Tag color="green">Standalone Mode (已加入主畫面)</Tag>
                  ) : (
                    <Tag color="blue">Browser Mode (Safari 瀏覽器)</Tag>
                  )}
                </div>
                <div>
                  <Text strong>設備類型: </Text>
                  <Text type="secondary">{deviceInfo.isIPad ? "iPad" : deviceInfo.isIOS ? "iPhone" : "其他"}</Text>
                </div>
                <div>
                  <Text strong>User Agent: </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {deviceInfo.userAgent}
                  </Text>
                </div>
              </Space>
            </div>

            <div>
              <Title level={5}>測試說明</Title>
              <Text type="secondary">
                點擊下方輸入框，觀察鍵盤彈起時頁面的適配效果。
                <br />
                ✅ 底部操作欄應自動上移，不被鍵盤遮擋
                <br />
                ✅ 輸入框應自動捲動到可見位置
                <br />✅ 頂部導航欄應保持固定
                <br />
                {isStandalone && (
                  <>
                    <br />
                    <Tag color="orange" style={{ marginTop: 8 }}>
                      Standalone Mode 已啟用特殊修復
                    </Tag>
                  </>
                )}
              </Text>
            </div>

            <Form layout="vertical">
              <Form.Item label="姓名（頂部輸入框）">
                <Input size="large" ref={input1Ref} placeholder="點擊此處輸入" />
              </Form.Item>

              <Form.Item label="電話">
                <Input size="large" ref={input2Ref} placeholder="輸入電話號碼" inputMode="numeric" />
              </Form.Item>

              <Form.Item label="郵箱">
                <Input size="large" ref={input3Ref} placeholder="輸入郵箱地址" type="email" />
              </Form.Item>

              <Form.Item label="地址">
                <Input.TextArea ref={input4Ref} placeholder="輸入詳細地址" rows={4} />
              </Form.Item>

              <Form.Item label="備註（底部輸入框）">
                <Input.TextArea ref={input5Ref} placeholder="點擊此處測試底部輸入框的捲動效果" rows={6} />
              </Form.Item>

              {/* 占位内容，使页面可滚动 */}
              <div style={{ height: 400, background: "#f0f0f0", borderRadius: 8, padding: 20 }}>
                <Text type="secondary">
                  佔位內容區域
                  <br />
                  用於測試頁面捲動功能
                  <br />
                  <br />
                  當前狀態：
                  <br />• 運行模式: {isStandalone ? "Standalone" : "Browser"}
                  <br />• 鍵盤高度: {keyboardHeight}px
                  <br />• 鍵盤可見: {isKeyboardVisible ? "是" : "否"}
                  <br />• Visual Viewport 高度: {viewportHeight}px
                  <br />• Window 高度: {window.innerHeight}px
                  <br />• 差值: {window.innerHeight - viewportHeight}px
                </Text>
              </div>
            </Form>
          </Space>
        </Card>
      </div>

      {/* 底部操作栏 - 应用键盘适配 */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          height: 80,
          background: "#fff",
          borderTop: "1px solid #e8e8e8",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 20px",
          zIndex: 100,
          boxShadow: "0 -2px 8px rgba(0,0,0,0.05)",
          transition: "bottom 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          // 键盘适配关键代码
          bottom: isKeyboardVisible ? keyboardHeight : 0,
        }}
      >
        <CustomButton
          height="40px"
          onClick={() => navigate(-1)}
          bgColor="#fff"
          color="#666"
          style={{ border: "1px solid #d9d9d9" }}
        >
          取消
        </CustomButton>
        <Space>
          <CustomButton
            height="40px"
            bgColor="#fff"
            color="#666"
            style={{ border: "1px solid #d9d9d9" }}
          >
            重置
          </CustomButton>
          <CustomButton
            bgColor="#08C4C8"
            height="40px"
          >
            儲存
          </CustomButton>
        </Space>
      </div>
    </div>
  );
}
